import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingCart, MessageCircle, Check } from "lucide-react";
import api, { formatMoney } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fireAddToCartConversion } from "@/lib/gtag";
import { toast } from "sonner";

export default function ProductoPublico() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState({ color: "", tela: "", tamano: "", estilo: "" });
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/product/${id}`);
        setP(data);
        // Preseleccionar primera opción de cada eje (normalizando string/objeto)
        const first = (arr) => {
          const v = (arr || [])[0];
          if (v == null) return "";
          if (typeof v === "string") return v;
          return String(v.nombre || v.label || v.value || "");
        };
        setVariant({
          color: first(data.opciones_color),
          tela: first(data.opciones_tela),
          tamano: first(data.opciones_tamano),
          estilo: first(data.opciones_estilo),
        });
      } catch (e) {
        toast.error("Producto no encontrado");
        navigate("/tienda");
      } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const handleAdd = () => {
    add(p, variant, qty);
    // Google Ads: conversión "Inclusión en el carrito" — tras confirmar que se agregó.
    fireAddToCartConversion();
    setAdded(true);
    toast.success(`${qty}× ${p.nombre} agregado al carrito`);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleOrderNow = () => {
    add(p, variant, qty);
    // Google Ads: conversión "Inclusión en el carrito" — también agrega antes de ir al carrito.
    fireAddToCartConversion();
    navigate("/carrito");
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-16 text-center text-zinc-500">Cargando…</div>;
  if (!p) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 md:py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-zinc-600 hover:text-[#E30613] mb-6" data-testid="back-to-tienda">
        <ArrowLeft className="w-4 h-4" /> Volver a la tienda
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Imagen */}
        <div className="bg-zinc-100 border-4 border-black aspect-square overflow-hidden">
          {p.imagen ? (
            <img src={p.imagen} alt={p.nombre} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-zinc-300 text-6xl font-black">CC</span>
            </div>
          )}
        </div>

        {/* Info + carrito */}
        <div>
          {p.categoria && (
            <div className="inline-block bg-[#FFD100] text-black text-[10px] font-black px-3 py-1 uppercase tracking-widest mb-3">
              {p.categoria}
            </div>
          )}
          <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight leading-tight" data-testid="product-title">{p.nombre}</h1>
          {(() => {
            const hasOferta = p.precio_oferta && p.precio_oferta > 0 && p.precio_oferta < p.precio;
            const precioActual = hasOferta ? p.precio_oferta : p.precio;
            const precioTachado = hasOferta ? p.precio : null;
            const descuento = precioTachado ? Math.round((1 - precioActual / precioTachado) * 100) : 0;
            return (
              <div className="mt-4 flex items-baseline gap-3 flex-wrap" data-testid="product-price">
                {precioTachado && (
                  <div className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-black">Antes</span>
                    <span className="text-base font-bold text-zinc-400 line-through -mt-1">{formatMoney(precioTachado)}</span>
                  </div>
                )}
                <span className="text-4xl md:text-5xl font-black text-[#E30613]">{formatMoney(precioActual)}</span>
                {descuento > 0 && <span className="bg-black text-[#FFD100] text-xs font-black px-2 py-1 uppercase">-{descuento}%</span>}
              </div>
            );
          })()}
          {p.descripcion && (
            <p className="mt-4 text-zinc-600">{p.descripcion}</p>
          )}

          {/* Variantes */}
          <div className="mt-6 space-y-4">
            {(p.opciones_color || []).length > 0 && (
              <VariantSelect label="Color" options={p.opciones_color} value={variant.color} onChange={(v) => setVariant({ ...variant, color: v })} testid="variant-color" />
            )}
            {(p.opciones_tela || []).length > 0 && (
              <VariantSelect label="Tela" options={p.opciones_tela} value={variant.tela} onChange={(v) => setVariant({ ...variant, tela: v })} testid="variant-tela" />
            )}
            {(p.opciones_tamano || []).length > 0 && (
              <VariantSelect label="Tamaño" options={p.opciones_tamano} value={variant.tamano} onChange={(v) => setVariant({ ...variant, tamano: v })} testid="variant-tamano" />
            )}
            {(p.opciones_estilo || []).length > 0 && (
              <VariantSelect label="Estilo" options={p.opciones_estilo} value={variant.estilo} onChange={(v) => setVariant({ ...variant, estilo: v })} testid="variant-estilo" />
            )}
          </div>

          {/* Cantidad */}
          <div className="mt-6">
            <label className="text-xs font-black uppercase tracking-widest text-zinc-500">Cantidad</label>
            <div className="flex items-center mt-2 border-2 border-black w-fit">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 font-black text-lg hover:bg-[#FFD100]" data-testid="qty-minus">−</button>
              <input type="number" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-14 h-10 text-center font-black text-lg border-x-2 border-black outline-none" data-testid="qty-input" />
              <button onClick={() => setQty(qty + 1)} className="w-10 h-10 font-black text-lg hover:bg-[#FFD100]" data-testid="qty-plus">+</button>
            </div>
          </div>

          {/* Botones */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAdd}
              className={`flex-1 font-black uppercase tracking-widest text-sm px-6 py-4 flex items-center justify-center gap-2 border-2 border-black transition-all ${added ? "bg-green-600 text-white border-green-600" : "bg-[#FFD100] text-black hover:bg-black hover:text-[#FFD100]"}`}
              data-testid="btn-add-cart"
            >
              {added ? <><Check className="w-5 h-5" /> Agregado</> : <><ShoppingCart className="w-5 h-5" /> Agregar</>}
            </button>
            <button
              onClick={handleOrderNow}
              className="flex-1 bg-[#E30613] hover:bg-red-700 text-white font-black uppercase tracking-widest text-sm px-6 py-4 flex items-center justify-center gap-2 transition-all border-2 border-[#E30613]"
              data-testid="btn-order-now"
            >
              <MessageCircle className="w-5 h-5" /> Pedir ahora
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VariantSelect({ label, options, value, onChange, testid }) {
  // options puede venir como strings o {nombre, precio_extra}
  const list = (options || []).map((o) => {
    if (o == null) return "";
    if (typeof o === "string") return o;
    return String(o.nombre || o.label || o.value || "");
  }).filter(Boolean);
  return (
    <div>
      <label className="text-xs font-black uppercase tracking-widest text-zinc-500">{label}</label>
      <div className="mt-2 flex flex-wrap gap-2">
        {list.map((o) => (
          <button
            key={o}
            onClick={() => onChange(o)}
            className={`px-4 py-2 border-2 text-sm font-bold uppercase tracking-tight transition-colors ${value === o ? "bg-black text-[#FFD100] border-black" : "bg-white text-black border-zinc-300 hover:border-black"}`}
            data-testid={`${testid}-${o}`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}
